import React, { useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

// Declara a biblioteca QRCode como global
declare const QRCode: any;

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: linear-gradient(135deg, #042E4B 0%, #0A4A6F 50%, #EF443C 100%);
  color: white;
  position: relative;
  overflow: hidden;
  padding: 4rem;
`;

const BackgroundPattern = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  opacity: 0.05;
  background-image: 
    radial-gradient(circle at 20% 50%, white 2px, transparent 2px),
    radial-gradient(circle at 80% 50%, white 2px, transparent 2px);
  background-size: 60px 60px;
`;

const Logo = styled(motion.div)`
  font-size: 8rem;
  font-weight: 900;
  color: white;
  text-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
  margin-bottom: 2rem;
  z-index: 1;
`;

const MainTagline = styled(motion.h2)`
  font-size: 3.5rem;
  font-weight: 700;
  color: white;
  margin-bottom: 4rem;
  text-align: center;
  max-width: 900px;
  line-height: 1.3;
  z-index: 1;
`;

const ContactAndQRSection = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 4rem;
  margin-bottom: 3rem;
  z-index: 1;
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: flex-start;
  font-size: 1.5rem;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  padding: 1rem 2rem;
  border-radius: 50px;
  border: 2px solid rgba(255, 255, 255, 0.3);
`;

const StatusBadge = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  background: rgba(34, 197, 94, 0.2);
  backdrop-filter: blur(10px);
  padding: 1.5rem 3rem;
  border-radius: 50px;
  font-size: 1.5rem;
  font-weight: 600;
  border: 2px solid #22C55E;
  z-index: 1;
`;

const QRCodeContainer = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 24px;
  box-shadow: 0 15px 50px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
`;

const QRCodePlaceholder = styled.div`
  width: 300px;
  height: 300px;
  background: white;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3rem;
`;

const QRCodeLabel = styled.div`
  color: #042E4B;
  font-size: 1.1rem;
  font-weight: 600;
  text-align: center;
`;

const FloatingIcon = styled(motion.div)<{ top: string; left: string }>`
  position: absolute;
  font-size: 4rem;
  opacity: 0.1;
  top: ${props => props.top};
  left: ${props => props.left};
`;

const Slide13CTA: React.FC = () => {
  const floatingIcons = [
    { emoji: '🎯', top: '10%', left: '10%' },
    { emoji: '📊', top: '20%', left: '85%' },
    { emoji: '🚀', top: '70%', left: '15%' },
    { emoji: '💡', top: '80%', left: '80%' },
    { emoji: '✅', top: '40%', left: '5%' },
    { emoji: '📈', top: '50%', left: '90%' },
  ];

  useEffect(() => {
    // Carrega a biblioteca QRCode.js dinamicamente
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js';
    script.async = true;
    script.onload = () => {
      // Gera o QR Code após o script carregar
      const qrcodeElement = document.getElementById('qrcode-demo');
      if (qrcodeElement && typeof QRCode !== 'undefined') {
        // Limpa o conteúdo anterior se existir
        qrcodeElement.innerHTML = '';
        
        // Detecta a URL base automaticamente
        const protocol = window.location.protocol;
        const host = window.location.host;
        const demoUrl = `${protocol}//${host}/tw/demo-login`;
        
        // Gera o QR Code
        const qrCodeInstance = new QRCode(qrcodeElement, {
          text: demoUrl,
          width: 300,
          height: 300,
          colorDark: '#042E4B',
          colorLight: '#ffffff',
          correctLevel: QRCode.CorrectLevel.H
        });
        
        // Usa a instância para evitar warning
        if (qrCodeInstance) {
          console.log('QR Code gerado com sucesso');
        }
      }
    };
    document.body.appendChild(script);

    // Cleanup
    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  return (
    <Container>
      <BackgroundPattern />

      {floatingIcons.map((icon) => (
        <FloatingIcon
          key={`${icon.emoji}-${icon.top}-${icon.left}`}
          top={icon.top}
          left={icon.left}
          animate={{
            y: [0, -20, 0],
            rotate: [0, 10, -10, 0],
          }}
          transition={{
            duration: 2,
            delay: floatingIcons.indexOf(icon) * 0.2,
            repeat: Infinity,
            repeatType: 'reverse',
          }}
        >
          {icon.emoji}
        </FloatingIcon>
      ))}

      <Logo
        initial={{ scale: 0.5, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.1 }}
      >
        ThinkWay
      </Logo>

      <MainTagline
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.25 }}
      >
        Planejamento Estratégico descomplicado e na ponta dos dedos
      </MainTagline>

      <ContactAndQRSection
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.4 }}
      >
        <ContactInfo>
          <ContactItem>
            <span>🌐</span>
            <span>thinkway.app</span>
          </ContactItem>
          <ContactItem>
            <span>📧</span>
            <span>contato@thinkway.app</span>
          </ContactItem>
        </ContactInfo>

        <QRCodeContainer>
          <QRCodePlaceholder id="qrcode-demo" />
          <QRCodeLabel>Escaneie para testar o demo</QRCodeLabel>
        </QRCodeContainer>
      </ContactAndQRSection>

      <StatusBadge
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.55 }}
      >
        <span>✅</span>
        <span>MVP Disponível</span>
        <span>|</span>
        <span>Comece Agora</span>
      </StatusBadge>
    </Container>
  );
};

export default Slide13CTA;
